import { Component, ViewEncapsulation, EventEmitter, HostBinding, OnDestroy, Input } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { catchError, map, Observable, Subscription } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRippleModule } from '@angular/material/core';
import { NgFor, NgIf } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';

import { pageTransitions } from '../../../shared/animations/page-transitions';
import { DatacenterType } from '../../../shared/models/datacenter-aggrid-view';
import { OrderBy, OrderByDirection, UNDERSCORE } from '../../../shared/utils/constant';
import { Document, FieldOfDocument } from '../../../commercial-documents/models/document';
import { DocumentService } from '../../../commercial-documents/services/documents.service';
import { User } from '../../../shared/models/user';
import { UserService } from '../../../shared/services/user.service';
import { SafeUrlPipe } from '../../../shared/pipes/safeurl.pipe';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { VirtualScrollerDirective } from '../../../shared/directives/virtual-scroller.directive';
import { BatchesService } from '../../services/batches.service';
import { OrderService } from '../../services/order.service';
import { DatacenterDefaultConfiguration } from '../../../shared/datacenter/datacenter-default-configuration';
import { ScheduleBusinessOrder } from '../../models/schedule-business-order';
import { ORDER_REDIRECT_URL, ScheduleBusinessOrderService } from '../../services/schedule-business-order.service';

const SORT_WAY = OrderByDirection.DESC;
const SORT_BY = 'date';

@Component({
	selector: 'copo-search-certificate-list',
	templateUrl: './search-certificate-list.component.html',
	styleUrl: './search-certificate-list.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		VirtualScrollerDirective,
		CdkScrollable,
		TranslateModule,
		NgFor,
		MatRippleModule,
		MatIconModule,

		MatButtonModule,
		RouterLink,
		NgIf,
		LoadingSpinnerComponent,
		DatePipe,
		SafeUrlPipe
	]
})
export class SearchCertificateListComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;

	@Input() document?: Document;
	readonly createFilter = new EventEmitter<{ document: Document; filterType: DatacenterType }>();
	readonly UNDERSCORE = UNDERSCORE;
	user: User;
	filter?: string | undefined;
	filters = '';
	documents: Document[] = [];
	scheduleBusinessOrderRequiredFields: (keyof ScheduleBusinessOrder)[] = ['supplierOrderType'];
	scheduleBusinessOrder?: Subscription;
	certificateRequiredFields: FieldOfDocument[] = [
		'customerOrderReference',
		'supplierOrderReference',
		'soldtoName',
		'shiptoName',
		'id',
		'type',
		'subtype',
		'date',
		'name',
		'paymentDueDate',
		'supplierVatNumber',
		'payerName',
		'payerVatNumber',
		'documentLines',
		'transportMode',
		'millCode'
	];

	private readonly searchTextSubscription?: Subscription;

	constructor(
		private readonly documentService: DocumentService,
		private readonly batchService: BatchesService,
		private readonly orderService: OrderService,
		private readonly router: Router,
		private readonly userService: UserService,
		private readonly activedRoute: ActivatedRoute,
		private readonly defaultConfiguration: DatacenterDefaultConfiguration,
		private readonly scheduleBusinessOrderService: ScheduleBusinessOrderService

	) {
		this.user = this.userService.user;
		if (!this.router.isActive(
			'search',
			{ paths: 'subset', queryParams: 'subset', fragment: 'ignored', matrixParams: 'ignored' }
		)) {
			return;
		}
		this.searchTextSubscription = this.activedRoute.params.subscribe(p => {
			const searchText = <string | undefined> p['query'];
			this.filter = searchText ? this.documentService.createLuceneFilter(searchText, 'CE') : undefined;
		});
	}

	get dataId() {
		return `search-certificate-list-${
			SORT_WAY
		}-${
			SORT_BY
		}--${
			this.filters
		}`;
	}

	ngOnDestroy() {
		if (this.searchTextSubscription) {
			this.searchTextSubscription.unsubscribe();
		}
		this.scheduleBusinessOrder?.unsubscribe();
	}

	onMoreDataNeeded(index: number, limit: number, provideData: (obs: Observable<Document[]>) => void) {
		provideData(
			this.documentService.getDocuments(
				this.certificateRequiredFields,
				this.filter,
				index,
				limit,
				new OrderBy(SORT_BY, SORT_WAY),
				false
			).pipe(
				map(res => {
					for (const documentObj of res.results) {
						if (this.user.hasServiceParameterEqualsTo('PORDT', 'SBOrder', 'True')) {
							this.scheduleBusinessOrderService.setRedirectionLink(
								documentObj,
								this.scheduleBusinessOrderRequiredFields
							);
						} else {
							documentObj.isSbOrder = false;
							documentObj.docNavigationLink = ORDER_REDIRECT_URL;
						}
						this.documents.push(documentObj);
					}
					return this.documents;
				}),
				catchError((err: unknown) => {
					console.error('error retrieving batches', err);
					return [];
				})
			)
		);
	}

	downloadDocument(id: number, filename: string) {
		return this.documentService.getDownloadDocumentUrl(id, filename);
	}

	getDocumentURL(id: number, filename: string) {
		return this.documentService.getViewDocumentUrl(id, filename);
	}

	async redirectToBatchDatacenter(document: Document) {
		await this.batchService.batchDatacenterFilter(document, this.defaultConfiguration.getDefaultView('batch'));
	}

	isSbOrder(document: Document) {
		const supplierOrderNumber = document.type === 'IN' || document.type === 'DA'
			? document.documentLines[0].supplierOrderReference
			: document.supplierOrderReference;
		this.scheduleBusinessOrder = this.scheduleBusinessOrderService.getScheduleBusinessOrderCard(
			supplierOrderNumber,
			this.scheduleBusinessOrderRequiredFields
		)
			.subscribe({
				next: res => res.supplierOrderType === 'E' || res.supplierOrderType === 'F',
				error: (error: unknown) => {
					console.error(error);
					return false;
				}
			});
	}

	async redirectToOrderDatacenter(document: Document) {
		if (document.isSbOrder) {
			await this.scheduleBusinessOrderService.sbOrderDatacenterFilter(
				document,
				this.defaultConfiguration.getDefaultView('schedule-business-order')
			);
			return;
		}
		await this.orderService.orderDatacenterFilter(
			document,
			this.defaultConfiguration.getDefaultView('order')
		);
	}
}
